<template>
  <div>
    <AwDatepicker
      v-model="monthState"
      :month-picker="true"
      :auto-apply="true"
      :formats="{ input: 'MM/yyyy' }"
      @update:model-value="(value) => handleUpdate(value as Month)"
    >
      <template #trigger>
        <AwButton variant="outline" color="secondary" class="month-filter-button" leading-icon="i-ph-calendar-dot">
          <div :class="monthFilterClasses">
            {{ formattedMonth }}
          </div>
        </AwButton>
      </template>
    </AwDatepicker>
  </div>
</template>

<script setup lang="ts">
type Month = Record<"month" | "year" | "day", number>;

type Props = {
  placeholder?: string;
};

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Depuis le début",
});

const modelValue = defineModel<Date | undefined>();

const monthState = ref<Month | undefined>(
  modelValue.value
    ? {
        year: modelValue.value.getFullYear(),
        month: modelValue.value.getMonth(),
        day: 1,
      }
    : undefined
);

const formattedMonth = computed(() => {
  if (!monthState.value) {
    return props.placeholder;
  }

  const date = new Date(monthState.value.year, monthState.value.month, 1, 2, 0, 0, 0);

  return date
    .toLocaleDateString("fr-FR", {
      month: "long",
    })
    .replace(/^\w/, (c) => c.toUpperCase());
});

const monthFilterClasses = computed(() => {
  return monthState.value ? "text-secondary-900 font-medium" : "font-medium";
});

const handleUpdate = (value: Month) => {
  const firstDayOfMonth = new Date(value.year, value.month, 1, 2, 0, 0, 0);
  modelValue.value = firstDayOfMonth;
};

watch(
  () => modelValue.value,
  (newMonth) => {
    if (newMonth) {
      monthState.value = {
        year: newMonth.getFullYear(),
        month: newMonth.getMonth(),
        day: 1,
      };
    } else {
      monthState.value = undefined;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.month-filter-button-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 34px;
}

.month-filter-button {
  height: 34px;
  color: var(--gray-3);
  border-radius: 6px;
  min-width: 95px;
}
</style>
